import axios from 'axios';
import { trace } from '@opentelemetry/api';
import { getQueryStringObject, checkSessionQueryParams } from '../utility/helper';
import { getConfig } from '../utility/env';
import { sharedConfig } from '../config/shared/config';
import { bootstrapHeaders } from '../utility/global-constants/queryKeys';
import { recordErrorBoundarySpan } from '../utility/otelSetup';

const bootstrapQueryParams = getQueryStringObject();
const env = getConfig();

export async function getContentData() {
  const fullContentRouteName = `${env.content.baseUrl}${sharedConfig.frictionLess.routes.content}`;
  const { data } = await axios.get(fullContentRouteName);
  return data.data;
}

export async function getBootstrapData() {
  const tracer = trace.getTracer('frictionless-ui');

  if (!checkSessionQueryParams()) {
    throw new Error('Missing or invalid session query parameters');
  }

  const fullBootstrapRouteName = `${env.eSign.baseUrl}${sharedConfig.frictionLess.routes.bootstrap}`;

  return tracer.startActiveSpan('frictionless.bootstrap', async (span) => {
    span.setAttribute('http.url', fullBootstrapRouteName);
    span.setAttribute('http.method', 'POST');
    try {
      const { data } = await axios.post(fullBootstrapRouteName, bootstrapQueryParams, { headers: bootstrapHeaders });
      span.setAttribute('http.status_code', 200);
      span.setAttribute('app.bootstrap.token_received', !!(data.data?.cimaToken?.accessToken));
      span.addEvent('bootstrap.success', { 'http.status_code': 200, 'app.bootstrap.token_received': !!(data.data?.cimaToken?.accessToken) });
      return data.data;
    } catch (error) {
      span.setAttribute('http.status_code', error?.response?.status || 0);
      span.setAttribute('error', true);
      span.addEvent('bootstrap.error', { 'http.status_code': error?.response?.status || 0, 'error.message': error?.message || '' });
      span.recordException(error);
      throw error;
    } finally {
      span.end();
    }
  });
}

export async function getInitializeData(token) {
  bootstrapHeaders['Authorization'] = token;
  const fullInitializeRouteName = `${env.eSign.baseUrl}${sharedConfig.frictionLess.routes.initialize}`;
  const tracer = trace.getTracer('frictionless-ui');

  return tracer.startActiveSpan('frictionless.initialize', async (span) => {
    span.setAttribute('http.url', fullInitializeRouteName);
    span.setAttribute('http.method', 'POST');
    try {
      const { data } = await axios.post(fullInitializeRouteName, bootstrapQueryParams, { headers: bootstrapHeaders });
      span.setAttribute('http.status_code', 200);
      span.setAttribute('app.initialize.session_id', data.data?.sessionId || '');
      span.setAttribute('app.initialize.order_submitted', data.data?.isOrderAlreadySubmited ?? false);
      span.addEvent('initialize.success', {
        'http.status_code': 200,
        'app.initialize.session_id': data.data?.sessionId || '',
        'app.initialize.order_submitted': data.data?.isOrderAlreadySubmited ?? false,
      });
      return data.data;
    } catch (error) {
      span.setAttribute('error', true);
      span.setAttribute('http.status_code', error?.response?.status || 0);
      span.addEvent('initialize.error', { 'http.status_code': error?.response?.status || 0, 'error.message': error?.message || '' });
      span.recordException(error);
      throw error;
    } finally {
      span.end();
    }
  });
}

export async function getUpdateOrder(token, sessionId) {
  bootstrapHeaders['Authorization'] = token;
  const fullUpdateRouteName = `${env.eSign.baseUrl}${sharedConfig.frictionLess.routes.update}?id=${sessionId}`;
  const tracer = trace.getTracer('frictionless-ui');

  return tracer.startActiveSpan('frictionless.update', async (span) => {
    span.setAttribute('http.url', fullUpdateRouteName);
    span.setAttribute('http.method', 'POST');
    span.setAttribute('app.update.session_id', sessionId || '');
    try {
      const { data } = await axios.post(fullUpdateRouteName, '', { headers: bootstrapHeaders });
      span.setAttribute('http.status_code', 200);
      span.addEvent('update.success', { 'http.status_code': 200 });
      return data;
    } catch (error) {
      if (error && error?.response?.status === 504) {
        // Added 5 Sec delay to update cache incase of 504 gateway timeout
        if (env?.updateCall504DelayEnabled) await new Promise((resolve) => setTimeout(resolve, 5000));
        try {
          const { data } = await axios.post(fullUpdateRouteName, '', { headers: bootstrapHeaders });
          span.setAttribute('http.status_code', 200);
          span.setAttribute('app.update.retried_after_504', true);
          span.addEvent('update.retried_after_504', { 'http.status_code': 200 });
          return data;
        } catch (retryError) {
          span.setAttribute('http.status_code', retryError?.response?.status || 504);
          span.setAttribute('error', true);
          span.addEvent('update.error', { 'http.status_code': retryError?.response?.status || 504, 'error.message': retryError?.message || '' });
          span.recordException(retryError);
          return retryError;
        }
      } else if (error && !error.response) {
        // handling CORS issue or error without response
        span.setAttribute('error', true);
        span.setAttribute('app.update.cors_or_network_error', true);
        span.addEvent('update.error', { 'error.message': 'CORS or network error — no response received' });
        span.recordException(error);
        return { response: { data: { status: 'Failure' } } };
      }
      span.setAttribute('http.status_code', error?.response?.status || 0);
      span.setAttribute('error', true);
      span.addEvent('update.error', { 'http.status_code': error?.response?.status || 0, 'error.message': error?.message || '' });
      span.recordException(error);
      return error;
    } finally {
      span.end();
    }
  });
}

export async function getCreateUser(token, userData) {
  bootstrapHeaders['Authorization'] = token;
  bootstrapHeaders['Content-Type'] = 'application/json';
  const fullCreateUserRouteName = `${env.eSign.baseUrl}${sharedConfig.frictionLess.routes.createUser}`;
  const { data } = await axios.post(fullCreateUserRouteName, userData, { headers: bootstrapHeaders });
  return data;
}

export async function getInviteUser(token, userData) {
  bootstrapHeaders['Authorization'] = token;
  bootstrapHeaders['Content-Type'] = 'application/json';
  const fullCreateUserRouteName = `${env.eSign.baseUrl}${sharedConfig.frictionLess.routes.inviteUser}`;
  const { data } = await axios.post(fullCreateUserRouteName, userData, { headers: bootstrapHeaders });
  return data.data;
}

export async function uploadDocument(requestBody, token) {
  bootstrapHeaders['Authorization'] = token;
  const fullUploadRouteName = `${env.eSign.baseUrl}${sharedConfig.frictionLess.routes.uploadDocument}`;
  const { data } = await axios.post(fullUploadRouteName, requestBody, { headers: bootstrapHeaders });
  return data;
}

export async function uploadDocumentToS3Bucket(requestBody, url) {
  const { data } = await axios.post(url, requestBody, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return data;
}

export async function uploadDocumentStatus(requestBody, token) {
  bootstrapHeaders['Authorization'] = token;
  const fullUploadRouteName = `${env.eSign.baseUrl}${sharedConfig.frictionLess.routes.updateStatus}`;
  const { data } = await axios.post(fullUploadRouteName, requestBody, { headers: bootstrapHeaders });
  return data;
}

export async function getOnboardingUrl(params) {
  const [cimaToken, sessionId] = params.queryKey;
  bootstrapHeaders['Authorization'] = cimaToken;
  bootstrapHeaders['Content-Type'] = 'application/json';
  const fullOnboardingUrlRouteName = `${env.eSign.baseUrl}${sharedConfig.frictionLess.routes.onboardingUrl}${sessionId}`;
  const { data } = await axios.get(fullOnboardingUrlRouteName, { headers: bootstrapHeaders });
  return data.data;
}

const logErrorBoundaryMessage = (message) => {
  recordErrorBoundarySpan(
    {
      name: 'ErrorBoundaryError',
      message: message || 'ErrorBoundary JS error',
    },
    {
      componentStack: '',
    }
  );
};

// Legacy SA-log API is intentionally disabled after OTel migration.
const logMessage = async () => Promise.resolve();

// Legacy SA-log API is intentionally disabled after OTel migration.
const serviceLogging = async () => Promise.resolve();

const api = {
  logErrorBoundaryMessage,
  logMessage,
  serviceLogging,
};

export default api;
